import {
  ArrowRightLeft,
  BadgeCheck,
  CheckCircle2,
  CreditCard,
  Gift,
  Inbox,
  KeyRound,
  Mail,
  MessageSquare,
  Phone,
  Receipt,
  ShieldCheck,
  Smartphone,
  Store,
  Tag,
  TrendingUp,
  UserCircle,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react'

import type { ToolCall, ToolResult } from '@/lib/api/support-chat-types'
import { UserChip } from './user-chip'

/**
 * Plain-English renderer for tool results. Each tool has its own branch that
 * pulls out the meaningful fields and renders a few short lines an operator
 * can read in 1-2 seconds. The raw JSON is hidden behind "View raw data" on
 * the parent ToolCallCard for the rare case someone needs it.
 */
export function ToolResultSummary({ toolCall }: { toolCall: ToolCall }) {
  const result = toolCall.result
  if (!result) return null
  if (!result.ok) {
    return <ErrorBody result={result} />
  }

  const data = result.data as Record<string, unknown>
  switch (toolCall.name) {
    case 'lookup_user':
      return <LookupUserSummary data={data} />
    case 'get_user_orders':
      return <UserOrdersSummary data={data} />
    case 'get_user_transactions':
      return <UserTransactionsSummary data={data} />
    case 'get_order':
      return <OrderDetailSummary data={data} />
    case 'sms_recent_messages':
      return <SmsRecentSummary data={data} />
    case 'sms_carrier_lookup':
      return <CarrierLookupSummary data={data} />
    case 'finix_recent_transactions':
      return <FinixRecentSummary data={data} />
    case 'resend_verification_sms':
      return <ResendSmsSummary data={data} />
    case 'regenerate_wallet_pass':
      return <RegenPassSummary data={data} />
    case 'clear_user_sessions':
      return <ClearSessionsSummary data={data} />
    case 'mark_order_redeemed':
      return <MarkRedeemedSummary data={data} />
    case 'refund_order':
      return <RefundOrderSummary data={data} />
    case 'transfer_tokens':
      return <TransferTokensSummary data={data} />
    case 'redeem_voucher_for_attendee':
      return <RedeemVoucherSummary data={data} />
    case 'update_user_contact':
      return <UpdateContactSummary data={data} />
    case 'lookup_vendor':
      return <VendorLookupSummary data={data} />
    case 'lookup_menu_item':
      return <MenuItemSummary data={data} />
    case 'get_vendor_revenue':
      return <VendorRevenueSummary data={data} />
    case 'get_top_vendors':
      return <TopVendorsSummary data={data} />
    case 'get_top_items':
      return <TopItemsSummary data={data} />
    case 'get_top_attendees':
      return <TopAttendeesSummary data={data} />
    case 'get_event_metrics':
      return <EventMetricsSummary data={data} />
    case 'list_vendor_admins':
      return <VendorAdminsSummary data={data} />
    case 'explain_user_permissions':
      return <PermissionsExplainSummary data={data} />
    case 'update_menu_item_price':
      return <UpdateItemPriceSummary data={data} />
    case 'create_menu_item':
      return <CreateMenuItemSummary data={data} />
    case 'create_vendor':
      return <CreateVendorSummary data={data} />
    case 'lookup_voucher_redemption':
      return <VoucherRedemptionSummary data={data} />
    case 'resend_vendor_admin_activation':
      return <ResendVendorActivationSummary data={data} />
    default:
      return <GenericSummary data={data} />
  }
}

// ============================================================================
// Generic helpers
// ============================================================================

function Row({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-1.5 text-[12px] leading-relaxed text-neutral-800 dark:text-neutral-100">
      {icon && <span className="mt-0.5 shrink-0 text-neutral-500 dark:text-neutral-400">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function MutedRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">{children}</p>
  )
}

function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'green' | 'amber' | 'red' | 'blue'
  children: React.ReactNode
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  )
}

function mask(value: unknown, keep = 4): string {
  const raw = typeof value === 'string' ? value : ''
  if (!raw) return ''
  const s = clean(raw)
  const tail = s.slice(-keep)
  return `••${tail}`
}

/**
 * Strip the `<untrusted>…</untrusted>` markers the backend wraps around
 * user/vendor-controlled string fields for prompt-injection protection.
 * Tags exist for the LLM's benefit — operators see clean text.
 *
 * Pattern mirrors `_TAG_PATTERN` on the backend: case-insensitive,
 * tolerant of internal whitespace (`< untrusted >`, newlines, tabs). The
 * backend already strips before sending to the LLM, so the frontend
 * should never see a malformed tag in practice — this is belt-and-
 * suspenders for cases where the backend wasn't run (mocks, fixtures).
 */
const UNTRUSTED_PATTERN = /<\/?\s*untrusted\s*>/gi
function clean(value: unknown): string {
  if (typeof value !== 'string' || !value) return ''
  return value.replace(UNTRUSTED_PATTERN, '')
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const sec = Math.round(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  return d.toLocaleDateString()
}

function cents(amountCents: unknown, currency: string = 'CAD'): string {
  const n = typeof amountCents === 'number' ? amountCents : 0
  return `${(n / 100).toLocaleString(undefined, { style: 'currency', currency })}`
}

function ErrorBody({ result }: { result: ToolResult }) {
  if (result.ok) return null
  const tone =
    result.error === 'permission_denied'
      ? 'red'
      : result.error === 'not_found'
      ? 'amber'
      : 'amber'
  return (
    <div className="space-y-1">
      <Row icon={<XCircle className="h-3.5 w-3.5" />}>
        <Pill tone={tone}>{result.error.replaceAll('_', ' ')}</Pill>
        <span className="ml-1.5">{result.message}</span>
      </Row>
    </div>
  )
}

// ============================================================================
// T1 — read-only lookups
// ============================================================================

interface LookupMatch {
  user_id: number
  first_name: string
  last_name: string
  email: string
  phone_number: string
  event_orders: number
  event_transactions: number
  /** ISO timestamp of attendee's first activity at this event, or null
   *  if none on record. Used to answer "when did X join" questions. */
  first_seen_at: string | null
}

function LookupUserSummary({ data }: { data: Record<string, unknown> }) {
  const matches = (data.matches as LookupMatch[] | undefined) ?? []
  const total = (data.total_matches as number | undefined) ?? matches.length
  const truncated = !!data.truncated

  if (matches.length === 0) {
    return <Row icon={<UserCircle className="h-3.5 w-3.5" />}>No matching attendee at this event.</Row>
  }

  if (matches.length === 1) {
    const m = matches[0]
    return (
      <div className="space-y-1">
        <Row icon={<UserCircle className="h-3.5 w-3.5" />}>
          <UserChip userId={m.user_id} name={`${clean(m.first_name)} ${clean(m.last_name)}`.trim()} />
        </Row>
        <MutedRow>
          {clean(m.email) || mask(m.phone_number)} · {m.event_orders} orders · {m.event_transactions} txns
          {m.first_seen_at ? <> · joined {formatRelative(m.first_seen_at)}</> : null}
        </MutedRow>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <Row icon={<Users className="h-3.5 w-3.5" />}>
        <span className="font-medium">{total} matches{truncated ? ' (showing 5)' : ''}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {matches.map((m) => (
          <li key={m.user_id}>
            <UserChip userId={m.user_id} name={`${clean(m.first_name)} ${clean(m.last_name)}`.trim()} />
            <span className="text-neutral-500 dark:text-neutral-400"> · {mask(m.phone_number)} · {m.event_orders} orders</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface OrderRow {
  id: number
  status: string
  vendor_name: string
  booth: string
  created_at: string
}

function statusTone(status: string) {
  if (['delivered', 'completed', 'accepted'].includes(status)) return 'green'
  if (['cancelled', 'refunded', 'failed'].includes(status)) return 'red'
  return 'neutral'
}

function UserOrdersSummary({ data }: { data: Record<string, unknown> }) {
  const orders = (data.orders as OrderRow[] | undefined) ?? []
  const count = (data.count as number | undefined) ?? orders.length

  if (orders.length === 0) {
    return <Row icon={<Receipt className="h-3.5 w-3.5" />}>No orders at this event.</Row>
  }

  return (
    <div className="space-y-1">
      <Row icon={<Receipt className="h-3.5 w-3.5" />}>
        <span className="font-medium">{count} order{count === 1 ? '' : 's'}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {orders.slice(0, 5).map((o) => {
          const vendor = clean(o.vendor_name)
          const booth = clean(o.booth)
          return (
            <li key={o.id}>
              #{o.id} · {vendor || 'Unknown vendor'}
              {booth ? ` (booth ${booth})` : ''} ·{' '}
              <Pill tone={statusTone(o.status) as 'green' | 'red' | 'neutral'}>{o.status}</Pill>
              <span className="ml-1 text-neutral-500 dark:text-neutral-400">{formatRelative(o.created_at)}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface TxnRow {
  id: number
  action: string
  amount: number | null
  timestamp: string
}

function UserTransactionsSummary({ data }: { data: Record<string, unknown> }) {
  const txns = (data.transactions as TxnRow[] | undefined) ?? []
  if (txns.length === 0) {
    return <Row icon={<Wallet className="h-3.5 w-3.5" />}>No token transactions at this event.</Row>
  }

  // Roll up counts by action
  const counts: Record<string, number> = {}
  let netTokens = 0
  for (const t of txns) {
    counts[t.action] = (counts[t.action] ?? 0) + 1
    netTokens += t.amount ?? 0
  }
  const POSITIVE = new Set(['addCredit', 'voucherCredit'])
  const NEGATIVE = new Set(['spendCredit', 'transfer', 'refund', 'partialRefund'])
  const positives = Object.entries(counts).filter(([k]) => POSITIVE.has(k))
  const negatives = Object.entries(counts).filter(([k]) => NEGATIVE.has(k))

  return (
    <div className="space-y-1">
      <Row icon={<Wallet className="h-3.5 w-3.5" />}>
        <span className="font-medium">{txns.length} transactions</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">
          net {netTokens >= 0 ? '+' : ''}{netTokens} tokens
        </span>
      </Row>
      {(positives.length > 0 || negatives.length > 0) && (
        <div className="flex flex-wrap gap-1 pl-5">
          {positives.map(([action, n]) => (
            <Pill key={action} tone="green">+{n} {action}</Pill>
          ))}
          {negatives.map(([action, n]) => (
            <Pill key={action} tone="amber">{n} {action}</Pill>
          ))}
        </div>
      )}
    </div>
  )
}

function OrderDetailSummary({ data }: { data: Record<string, unknown> }) {
  const id = data.id as number | undefined
  const status = (data.status as string | undefined) ?? 'unknown'
  const vendor = clean(data.vendor_name as string | undefined) || 'Unknown vendor'
  const booth = clean(data.booth as string | undefined)
  const itemCount = (data.item_count as number | undefined) ?? 0
  const note = clean(data.note as string | undefined)

  return (
    <div className="space-y-1">
      <Row icon={<Receipt className="h-3.5 w-3.5" />}>
        <span className="font-medium">Order #{id}</span>
        <span className="ml-1.5">at {vendor}{booth ? ` (booth ${booth})` : ''}</span>
      </Row>
      <Row>
        <Pill tone={statusTone(status) as 'green' | 'red' | 'neutral'}>{status}</Pill>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </span>
      </Row>
      {note && <MutedRow>Note: {note}</MutedRow>}
    </div>
  )
}

interface SmsMessage {
  provider: string
  status: string
  error_code?: string | null
  sent_at?: string
}

function SmsRecentSummary({ data }: { data: Record<string, unknown> }) {
  const messages = (data.messages as SmsMessage[] | undefined) ?? []
  const count = (data.count as number | undefined) ?? messages.length

  if (messages.length === 0) {
    return <Row icon={<MessageSquare className="h-3.5 w-3.5" />}>No SMS sent in this window.</Row>
  }

  const failed = messages.filter((m) => m.status === 'failed' || m.status === 'undelivered').length
  const truncated = data.truncated === true
  const last = messages[0]
  const lastStatusTone =
    last.status === 'delivered' ? 'green' : last.status === 'sent' || last.status === 'queued' ? 'blue' : 'amber'

  return (
    <div className="space-y-1">
      <Row icon={<MessageSquare className="h-3.5 w-3.5" />}>
        <span className="font-medium">{count} SMS</span>
        {truncated && <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">(latest — older exist)</span>}
        {failed > 0 && <span className="ml-1.5 text-amber-700 dark:text-amber-400">{failed} failed</span>}
      </Row>
      <Row>
        <span className="text-neutral-500 dark:text-neutral-400">last:</span>
        <Pill tone={lastStatusTone as 'green' | 'amber' | 'blue'}>{last.status}</Pill>
        {last.error_code && <span className="ml-1 text-amber-700 dark:text-amber-400">err {last.error_code}</span>}
        {last.sent_at && (
          <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">· {formatRelative(last.sent_at)}</span>
        )}
      </Row>
    </div>
  )
}

function CarrierLookupSummary({ data }: { data: Record<string, unknown> }) {
  const lineType = (data.line_type as string | undefined) ?? 'unknown'
  const carrier = (data.carrier_name as string | undefined) ?? ''
  const country = (data.country as string | undefined) ?? ''

  const voipy = lineType === 'voip' || lineType === 'fixed_voip'
  return (
    <div className="space-y-1">
      <Row icon={<Phone className="h-3.5 w-3.5" />}>
        <Pill tone={voipy ? 'amber' : 'neutral'}>{lineType}</Pill>
        <span className="ml-1.5">{carrier || 'Unknown carrier'}</span>
        {country && <span className="ml-1 text-neutral-500 dark:text-neutral-400">({country})</span>}
      </Row>
      {voipy && (
        <MutedRow>VOIP numbers often filter OTPs. Suggest a real mobile number.</MutedRow>
      )}
    </div>
  )
}

interface FinixSlim {
  transaction_id?: string
  amount_cents?: number
  currency?: string
  created_at?: string
  failure_code?: string
}

function FinixRecentSummary({ data }: { data: Record<string, unknown> }) {
  const total = (data.total_attempts as number | undefined) ?? 0
  const stateCounts = (data.state_counts as Record<string, number> | undefined) ?? {}
  const failureCounts = (data.failure_counts as Record<string, number> | undefined) ?? {}
  const failureHints = (data.failure_hints as Record<string, string> | undefined) ?? {}
  const recentFailures = (data.recent_failures as FinixSlim[] | undefined) ?? []
  const recentSuccesses = (data.recent_successes as FinixSlim[] | undefined) ?? []

  if (total === 0) {
    const note = data.note as string | undefined
    return (
      <Row icon={<CreditCard className="h-3.5 w-3.5" />}>
        {note || 'No Finix attempts in this window.'}
      </Row>
    )
  }

  const succeeded = stateCounts.SUCCEEDED ?? 0
  const failed = stateCounts.FAILED ?? 0
  return (
    <div className="space-y-1.5">
      <Row icon={<CreditCard className="h-3.5 w-3.5" />}>
        <span className="font-medium">{total} card attempts</span>
        <span className="ml-1.5">
          <Pill tone="green">{succeeded} ok</Pill>
          {failed > 0 && (
            <>
              {' '}
              <Pill tone="red">{failed} failed</Pill>
            </>
          )}
        </span>
      </Row>

      {Object.keys(failureCounts).length > 0 && (
        <div className="pl-5">
          <p className="text-[11px] font-medium text-neutral-700 dark:text-neutral-200">Why they failed:</p>
          <ul className="mt-0.5 space-y-0.5 text-[11px] text-neutral-700 dark:text-neutral-200">
            {Object.entries(failureCounts).map(([code, n]) => (
              <li key={code}>
                <span className="font-mono text-[10px]">{code}</span>
                <span className="ml-1 text-neutral-500 dark:text-neutral-400">×{n}</span>
                {failureHints[code] && (
                  <div className="ml-3 text-neutral-500 dark:text-neutral-400">{failureHints[code]}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentFailures.length > 0 && (
        <div className="pl-5">
          <p className="text-[11px] font-medium text-neutral-700 dark:text-neutral-200">Recent failures:</p>
          <ul className="mt-0.5 space-y-0.5 text-[11px] text-neutral-700 dark:text-neutral-200">
            {recentFailures.map((f) => (
              <li key={f.transaction_id}>
                {cents(f.amount_cents, f.currency || 'CAD')}{' '}
                <span className="text-neutral-500 dark:text-neutral-400">· {formatRelative(f.created_at)}</span>
                {f.failure_code && (
                  <span className="ml-1 font-mono text-[10px] text-red-600 dark:text-red-400">{f.failure_code}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentSuccesses.length > 0 && (
        <MutedRow>
          Last success: {cents(recentSuccesses[0].amount_cents, recentSuccesses[0].currency || 'CAD')} ·{' '}
          {formatRelative(recentSuccesses[0].created_at)}
        </MutedRow>
      )}
    </div>
  )
}

// ============================================================================
// T2 — recoverable mutations
// ============================================================================

function ResendSmsSummary({ data }: { data: Record<string, unknown> }) {
  const phone = data.phone_number as string | undefined
  return (
    <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
      Verification SMS sent to {phone || 'attendee'}.
    </Row>
  )
}

function RegenPassSummary({ data }: { data: Record<string, unknown> }) {
  const oldCount = (data.previous_barcodes_invalidated as number | undefined) ?? 0
  return (
    <div className="space-y-0.5">
      <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
        New wallet pass issued.
      </Row>
      {oldCount > 0 && <MutedRow>{oldCount} previous barcode{oldCount === 1 ? '' : 's'} invalidated.</MutedRow>}
    </div>
  )
}

function ClearSessionsSummary({ data }: { data: Record<string, unknown> }) {
  const n = (data.tokens_revoked as number | undefined) ?? 0
  return (
    <div className="space-y-0.5">
      <Row icon={<KeyRound className="h-3.5 w-3.5" />}>
        {n} session{n === 1 ? '' : 's'} revoked. Attendee will be logged out.
      </Row>
      <MutedRow>They&apos;ll need to log in again on their next visit.</MutedRow>
    </div>
  )
}

// ============================================================================
// T3 — destructive (post-confirm result rendering)
// ============================================================================

function MarkRedeemedSummary({ data }: { data: Record<string, unknown> }) {
  const id = data.order_id as number | undefined
  return (
    <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
      Order #{id} marked delivered.
    </Row>
  )
}

function RefundOrderSummary({ data }: { data: Record<string, unknown> }) {
  const id = data.order_id as number | undefined
  const amount = data.amount as number | undefined
  return (
    <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
      Order #{id} {amount ? `refunded ${amount} tokens` : 'refunded in full'}.
    </Row>
  )
}

function TransferTokensSummary({ data }: { data: Record<string, unknown> }) {
  const amt = data.amount as number | undefined
  const from = data.from_user_id as number | undefined
  const to = data.to_user_id as number | undefined
  return (
    <div className="space-y-0.5">
      <Row icon={<ArrowRightLeft className="h-3.5 w-3.5" />}>
        Transferred {amt} tokens · user #{from} → #{to}
      </Row>
      <MutedRow>
        Source balance: {String(data.from_balance_after ?? '?')} · destination: {String(data.to_balance_after ?? '?')}
      </MutedRow>
    </div>
  )
}

function RedeemVoucherSummary({ data }: { data: Record<string, unknown> }) {
  const code = data.voucher_code as string | undefined
  const message = data.message as string | undefined
  return (
    <Row icon={<Gift className="h-3.5 w-3.5" />}>
      Voucher <span className="font-mono text-[11px]">{code}</span> redeemed.
      {message && <span className="ml-1 text-neutral-500 dark:text-neutral-400">{message}</span>}
    </Row>
  )
}

function UpdateContactSummary({ data }: { data: Record<string, unknown> }) {
  if (data.changed === false) {
    return <MutedRow>{(data.note as string) || 'No changes — values matched.'}</MutedRow>
  }
  const fields = (data.fields_updated as string[] | undefined) ?? []
  return (
    <div className="space-y-0.5">
      <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
        Updated {fields.join(' & ')}.
      </Row>
      {fields.includes('email') && (
        <MutedRow>
          <Mail className="inline h-3 w-3 mr-0.5" />
          {clean(String(data.old_email || '—'))} → {clean(String(data.new_email || '—'))}
        </MutedRow>
      )}
      {fields.includes('phone_number') && (
        <MutedRow>
          <Smartphone className="inline h-3 w-3 mr-0.5" />
          {String(data.old_phone || '—')} → {String(data.new_phone || '—')}
        </MutedRow>
      )}
    </div>
  )
}

// ============================================================================
// Vendor / menu / admin lookups
// ============================================================================

interface VendorMatch {
  vendor_id: number
  vendor_name: string
  vendor_event_id: number
  booth: string
  is_approved: boolean
}

function VendorLookupSummary({ data }: { data: Record<string, unknown> }) {
  const matches = (data.matches as VendorMatch[] | undefined) ?? []
  const suggestions = (data.suggestions as VendorMatch[] | undefined) ?? []
  const total = (data.total_matches as number | undefined) ?? matches.length

  if (matches.length === 0 && suggestions.length > 0) {
    return (
      <div className="space-y-1">
        <Row icon={<Store className="h-3.5 w-3.5 text-amber-600" />}>
          <span className="font-medium">No exact match — did you mean…</span>
        </Row>
        <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
          {suggestions.map((v) => (
            <li key={v.vendor_id}>
              <span className="font-medium">{clean(v.vendor_name)}</span>
              <span className="text-neutral-500 dark:text-neutral-400"> · #{v.vendor_id}</span>
              {v.booth && (
                <span className="text-neutral-500 dark:text-neutral-400"> · booth {clean(v.booth)}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  if (matches.length === 0) {
    return <Row icon={<Store className="h-3.5 w-3.5" />}>No vendor matches.</Row>
  }

  if (matches.length === 1) {
    const v = matches[0]
    return (
      <Row icon={<Store className="h-3.5 w-3.5" />}>
        <span className="font-medium">{clean(v.vendor_name)}</span>
        <span className="ml-1 text-neutral-500 dark:text-neutral-400">#{v.vendor_id}</span>
        {v.booth && (
          <span className="ml-1 text-neutral-500 dark:text-neutral-400">· booth {clean(v.booth)}</span>
        )}
        {!v.is_approved && <span className="ml-1"><Pill tone="amber">unapproved</Pill></span>}
      </Row>
    )
  }

  return (
    <div className="space-y-1">
      <Row icon={<Store className="h-3.5 w-3.5" />}>
        <span className="font-medium">{total} vendor{total === 1 ? '' : 's'}</span>
        {data.truncated ? (
          <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">(showing 5)</span>
        ) : null}
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {matches.map((v) => (
          <li key={v.vendor_id}>
            <span className="font-medium">{clean(v.vendor_name)}</span>
            <span className="text-neutral-500 dark:text-neutral-400"> · #{v.vendor_id}</span>
            {v.booth && (
              <span className="text-neutral-500 dark:text-neutral-400"> · booth {clean(v.booth)}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface MenuItemMatch {
  item_id: number
  name: string
  price: number
  discount_price: number | null
  is_available: boolean
  hide_from_menu: boolean
  direct_pay: boolean
  vendor_name: string
  vendor_id: number | null
  booth: string
}

interface MenuItemSuggestion {
  item_id: number
  name: string
  price: number
  vendor_name: string
  vendor_id: number | null
  booth: string
}

function MenuItemSummary({ data }: { data: Record<string, unknown> }) {
  const matches = (data.matches as MenuItemMatch[] | undefined) ?? []
  const suggestions = (data.suggestions as MenuItemSuggestion[] | undefined) ?? []

  if (matches.length === 0 && suggestions.length > 0) {
    return (
      <div className="space-y-1">
        <Row icon={<Tag className="h-3.5 w-3.5 text-amber-600" />}>
          <span className="font-medium">No exact match — did you mean…</span>
        </Row>
        <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
          {suggestions.map((s) => (
            <li key={s.item_id}>
              <span className="font-medium">{clean(s.name)}</span>
              <span className="ml-1 tabular-nums">{s.price} tokens</span>
              <span className="text-neutral-500 dark:text-neutral-400"> · {clean(s.vendor_name) || 'Unknown vendor'}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }
  const total = (data.total_matches as number | undefined) ?? matches.length

  if (matches.length === 0) {
    return <Row icon={<Tag className="h-3.5 w-3.5" />}>No menu item matches.</Row>
  }

  return (
    <div className="space-y-1">
      <Row icon={<Tag className="h-3.5 w-3.5" />}>
        <span className="font-medium">{total} item{total === 1 ? '' : 's'}</span>
        {data.truncated ? (
          <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">(showing 5)</span>
        ) : null}
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {matches.map((m) => (
          <li key={m.item_id}>
            <span className="font-medium">{clean(m.name)}</span>
            <span className="ml-1 tabular-nums">{m.price} tokens</span>
            {m.discount_price ? (
              <span className="ml-1 text-emerald-700 dark:text-emerald-400">
                (sale: {m.discount_price})
              </span>
            ) : null}
            <span className="text-neutral-500 dark:text-neutral-400">
              {' · '}
              {clean(m.vendor_name) || 'Unknown vendor'}
              {m.booth ? ` (booth ${clean(m.booth)})` : ''}
            </span>
            {!m.is_available && <span className="ml-1"><Pill tone="amber">unavailable</Pill></span>}
            {m.hide_from_menu && <span className="ml-1"><Pill tone="neutral">hidden</Pill></span>}
            {m.direct_pay && <span className="ml-1"><Pill tone="blue">direct pay</Pill></span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

function VendorRevenueSummary({ data }: { data: Record<string, unknown> }) {
  const vendor = clean(data.vendor_name as string | undefined) || `Vendor #${data.vendor_id}`
  const tokens = (data.revenue_tokens as number | undefined) ?? 0
  const orders = (data.total_orders as number | undefined) ?? 0
  const items = (data.items_sold as number | undefined) ?? 0
  const hours = data.hours as number | null | undefined
  const windowLabel =
    (data.window as string | undefined) ||
    (hours == null ? 'all-time' : `last ${hours}h`)
  const counts = (data.order_status_counts as Record<string, number> | undefined) ?? {}

  return (
    <div className="space-y-1">
      <Row icon={<TrendingUp className="h-3.5 w-3.5" />}>
        <span className="font-medium">{vendor}</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">{windowLabel}</span>
      </Row>
      <Row>
        <span className="tabular-nums font-medium">{tokens.toLocaleString()} tokens</span>
        <span className="mx-1.5 text-neutral-500 dark:text-neutral-400">·</span>
        <span className="tabular-nums">{orders} orders</span>
        <span className="mx-1.5 text-neutral-500 dark:text-neutral-400">·</span>
        <span className="tabular-nums">{items} items</span>
      </Row>
      {Object.keys(counts).length > 0 && (
        <div className="flex flex-wrap gap-1 pl-5">
          {Object.entries(counts).map(([status, n]) => (
            <Pill key={status} tone={statusTone(status) as 'green' | 'red' | 'neutral'}>
              {n} {status}
            </Pill>
          ))}
        </div>
      )}
    </div>
  )
}

interface TopVendorRow {
  rank: number
  vendor_id: number
  vendor_name: string
  booth: string
  total_orders: number
  revenue_tokens: number
  items_sold: number
}

function TopVendorsSummary({ data }: { data: Record<string, unknown> }) {
  const top = (data.top as TopVendorRow[] | undefined) ?? []
  const metric = (data.metric as string | undefined) ?? 'orders'
  const windowLabel =
    (data.window as string | undefined) ||
    (data.hours == null ? 'all-time' : `last ${data.hours}h`)
  const note = data.note as string | undefined

  if (top.length === 0) {
    return (
      <div className="space-y-0.5">
        <Row icon={<TrendingUp className="h-3.5 w-3.5" />}>
          No completed orders to rank.
        </Row>
        {note && <MutedRow>{note}</MutedRow>}
      </div>
    )
  }

  const headlineFor = (row: TopVendorRow) => {
    if (metric === 'revenue') return `${row.revenue_tokens.toLocaleString()} tokens`
    if (metric === 'items') return `${row.items_sold.toLocaleString()} items`
    return `${row.total_orders.toLocaleString()} orders`
  }

  return (
    <div className="space-y-1">
      <Row icon={<TrendingUp className="h-3.5 w-3.5" />}>
        <span className="font-medium">Top {top.length} by {metric}</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">{windowLabel}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {top.map((row) => (
          <li key={row.vendor_id}>
            <span className="font-medium">#{row.rank} {clean(row.vendor_name) || `Vendor #${row.vendor_id}`}</span>
            {row.booth && (
              <span className="text-neutral-500 dark:text-neutral-400"> · booth {clean(row.booth)}</span>
            )}
            <span className="ml-1 tabular-nums">{headlineFor(row)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface TopItemRow {
  rank: number
  item_id: number
  item_name: string
  vendor_id: number
  vendor_name: string
  booth: string
  units_sold: number
  revenue_tokens: number
  order_count: number
}

function TopItemsSummary({ data }: { data: Record<string, unknown> }) {
  const top = (data.top as TopItemRow[] | undefined) ?? []
  const metric = (data.metric as string | undefined) ?? 'units'
  const windowLabel =
    (data.window as string | undefined) ||
    (data.hours == null ? 'all-time' : `last ${data.hours}h`)
  const note = data.note as string | undefined

  if (top.length === 0) {
    return (
      <div className="space-y-0.5">
        <Row icon={<TrendingUp className="h-3.5 w-3.5" />}>
          No completed orders to rank.
        </Row>
        {note && <MutedRow>{note}</MutedRow>}
      </div>
    )
  }

  const headlineFor = (row: TopItemRow) => {
    if (metric === 'revenue') return `${row.revenue_tokens.toLocaleString()} tokens`
    if (metric === 'orders') return `${row.order_count.toLocaleString()} orders`
    return `${row.units_sold.toLocaleString()} units`
  }

  return (
    <div className="space-y-1">
      <Row icon={<Tag className="h-3.5 w-3.5" />}>
        <span className="font-medium">Top {top.length} items by {metric}</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">{windowLabel}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {top.map((row) => (
          <li key={row.item_id}>
            <span className="font-medium">#{row.rank} {clean(row.item_name) || `Item #${row.item_id}`}</span>
            {row.vendor_name && (
              <span className="text-neutral-500 dark:text-neutral-400"> · {clean(row.vendor_name)}</span>
            )}
            <span className="ml-1 tabular-nums">{headlineFor(row)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface TopAttendeeRow {
  rank: number
  user_id: number
  name: string
  email: string
  phone_number: string
  value: number
  metric_label: string
  order_count?: number
  /** Legacy field from older results, before rank_by was added. */
  tokens_spent?: number
}

function unitWord(metricLabel: string): string {
  const m = metricLabel.toLowerCase()
  if (m.includes('token')) return 'tokens'
  if (m.includes('order')) return 'orders'
  if (m.includes('voucher')) return 'vouchers'
  return ''
}

function TopAttendeesSummary({ data }: { data: Record<string, unknown> }) {
  const top = (data.top as TopAttendeeRow[] | undefined) ?? []
  const metricLabel =
    (data.metric_label as string | undefined) || 'tokens spent'
  const windowLabel =
    (data.window as string | undefined) ||
    (data.hours == null ? 'all-time' : `last ${data.hours}h`)
  const note = data.note as string | undefined

  if (top.length === 0) {
    return (
      <div className="space-y-0.5">
        <Row icon={<UserCircle className="h-3.5 w-3.5" />}>
          No {metricLabel} recorded.
        </Row>
        {note && <MutedRow>{note}</MutedRow>}
      </div>
    )
  }

  const unit = unitWord(metricLabel)
  return (
    <div className="space-y-1">
      <Row icon={<UserCircle className="h-3.5 w-3.5" />}>
        <span className="font-medium">Top {top.length} by {metricLabel}</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">{windowLabel}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {top.map((row) => {
          const value = row.value ?? row.tokens_spent ?? 0
          return (
            <li key={row.user_id}>
              <span className="text-neutral-500 dark:text-neutral-400">#{row.rank}</span>{' '}
              <UserChip userId={row.user_id} name={clean(row.name)} variant="inline" />
              <span className="ml-1.5 tabular-nums">
                {value.toLocaleString()}
                {unit ? ` ${unit}` : ''}
              </span>
              {row.order_count != null && unit !== 'orders' && (
                <span className="text-neutral-500 dark:text-neutral-400">
                  {' '}· {row.order_count.toLocaleString()} order{row.order_count === 1 ? '' : 's'}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function EventMetricsSummary({ data }: { data: Record<string, unknown> }) {
  const value = (data.value as number | undefined) ?? 0
  const label = (data.label as string | undefined) || 'result'
  const unit = (data.unit as string | undefined) || ''
  const windowLabel = (data.window as string | undefined) || 'all-time'

  const icon =
    unit === 'people' ? <Users className="h-3.5 w-3.5" /> :
    unit === 'orders' ? <Receipt className="h-3.5 w-3.5" /> :
    unit === 'redemptions' ? <Gift className="h-3.5 w-3.5" /> :
    unit === 'tokens' ? <Wallet className="h-3.5 w-3.5" /> :
    <TrendingUp className="h-3.5 w-3.5" />

  return (
    <div className="space-y-0.5">
      <Row icon={icon}>
        <span className="font-medium tabular-nums">{value.toLocaleString()}</span>
        <span className="ml-1.5">{label}</span>
      </Row>
      <MutedRow>{windowLabel}</MutedRow>
    </div>
  )
}

interface VendorAdminRow {
  type: 'vendor_admin' | 'vendor_event_admin'
  scope: string
  user_id: number
  name: string
  email: string
  is_active: boolean
  last_login: string | null
}

function VendorAdminsSummary({ data }: { data: Record<string, unknown> }) {
  const admins = (data.admins as VendorAdminRow[] | undefined) ?? []
  const count = (data.admin_count as number | undefined) ?? admins.length

  if (admins.length === 0) {
    return (
      <Row icon={<Store className="h-3.5 w-3.5" />}>
        No admins on file for this vendor at this event.
      </Row>
    )
  }

  return (
    <div className="space-y-1">
      <Row icon={<Store className="h-3.5 w-3.5" />}>
        <span className="font-medium">{count} admin{count === 1 ? '' : 's'}</span>
      </Row>
      <ul className="space-y-0.5 pl-5 text-[11px] text-neutral-700 dark:text-neutral-200">
        {admins.map((a) => (
          <li key={`${a.type}-${a.user_id}`}>
            <UserChip
              userId={a.user_id}
              name={clean(a.name) || clean(a.email)}
              variant="inline"
            />{' '}
            <Pill tone={a.type === 'vendor_admin' ? 'blue' : 'neutral'}>
              {a.type === 'vendor_admin' ? 'all events' : 'this event only'}
            </Pill>
            {!a.is_active && <span className="ml-1"><Pill tone="amber">inactive</Pill></span>}
            {a.last_login ? (
              <span className="ml-1 text-neutral-500 dark:text-neutral-400">
                last login {formatRelative(a.last_login)}
              </span>
            ) : (
              <span className="ml-1 text-amber-700 dark:text-amber-400">never logged in</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface PermissionSource {
  type: 'organization_admin' | 'event_admin' | 'vendor_admin' | 'vendor_event_admin'
  permissions?: string[]
  groups?: string[]
  vendor_name?: string
  organization_name?: string
  grants?: string
}

function PermissionsExplainSummary({ data }: { data: Record<string, unknown> }) {
  const name = clean(data.name as string | undefined)
  const userId = typeof data.user_id === 'number' ? data.user_id : null
  const sources = (data.sources as PermissionSource[] | undefined) ?? []
  const note = data.note as string | undefined

  // Reusable header that renders the user as a clickable chip when we have
  // an id, falling back to plain text otherwise.
  const subject = userId !== null
    ? <UserChip userId={userId} name={name} variant="inline" />
    : <span className="font-medium">{name || 'User'}</span>

  if (data.is_superuser) {
    return (
      <Row icon={<ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}>
        {subject} is a superuser — all permissions, all events.
      </Row>
    )
  }

  if (sources.length === 0) {
    return (
      <div className="space-y-0.5">
        <Row icon={<BadgeCheck className="h-3.5 w-3.5" />}>
          {subject} has no admin role at this event.
        </Row>
        {note && <MutedRow>{note}</MutedRow>}
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Row icon={<BadgeCheck className="h-3.5 w-3.5" />}>
        {subject} · {sources.length} role{sources.length === 1 ? '' : 's'} at this event
      </Row>
      <ul className="space-y-1.5 pl-5 text-[11px]">
        {sources.map((s, idx) => (
          <li key={idx}>
            <div className="font-medium text-neutral-800 dark:text-neutral-100">
              {sourceLabel(s)}
            </div>
            {s.grants && (
              <div className="text-neutral-500 dark:text-neutral-400">{s.grants}</div>
            )}
            {s.groups && s.groups.length > 0 && (
              <div className="text-neutral-500 dark:text-neutral-400">
                Groups: {s.groups.join(', ')}
              </div>
            )}
            {s.permissions && s.permissions.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {s.permissions.slice(0, 8).map((p) => (
                  <Pill key={p}>{p}</Pill>
                ))}
                {s.permissions.length > 8 && (
                  <span className="text-neutral-500 dark:text-neutral-400">
                    +{s.permissions.length - 8} more
                  </span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function UpdateItemPriceSummary({ data }: { data: Record<string, unknown> }) {
  const name = clean(data.name as string | undefined) || `Item #${data.item_id}`
  const oldPrice = data.old_price as number | undefined
  const newPrice = data.new_price as number | undefined
  const changed = data.changed !== false

  if (!changed) {
    return (
      <Row icon={<Tag className="h-3.5 w-3.5" />}>
        <span className="font-medium">{name}</span>
        <span className="ml-1.5 text-neutral-500 dark:text-neutral-400">no change — price was already {newPrice}</span>
      </Row>
    )
  }

  return (
    <div className="space-y-0.5">
      <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
        <span className="font-medium">{name}</span>
        <span className="ml-1.5 tabular-nums">{oldPrice} → {newPrice} tokens</span>
      </Row>
      {data.vendor_name ? (
        <MutedRow>at {clean(String(data.vendor_name))}</MutedRow>
      ) : null}
    </div>
  )
}

function ResendVendorActivationSummary({ data }: { data: Record<string, unknown> }) {
  const email = clean(data.email as string | undefined)
  const vendor = clean(data.vendor_name as string | undefined)
  return (
    <Row icon={<Mail className="h-3.5 w-3.5 text-emerald-600" />}>
      Activation email re-sent to {email || 'vendor admin'}
      {vendor ? <span className="text-neutral-500 dark:text-neutral-400"> for {vendor}</span> : null}.
    </Row>
  )
}

function CreateMenuItemSummary({ data }: { data: Record<string, unknown> }) {
  const name = clean(data.name as string | undefined) || `Item #${data.item_id}`
  const vendor = clean(data.vendor_name as string | undefined)
  const booth = clean(data.booth as string | undefined)
  const price = typeof data.price === 'number' ? data.price : undefined
  const isAvailable = data.is_available !== false
  const isDirectPay = !!data.is_direct_pay
  const dietary = (data.dietary as Record<string, boolean> | undefined) || {}
  const dietaryFlags = (
    [
      ['vegetarian', 'Vegetarian'],
      ['vegan', 'Vegan'],
      ['glutenfree', 'GF'],
      ['dairyfree', 'Dairy-free'],
      ['beverage', 'Beverage'],
      ['alcoholic', 'Alcoholic'],
    ] as const
  ).filter(([key]) => dietary[key]).map(([, label]) => label)

  return (
    <div className="space-y-0.5">
      <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
        <span className="font-medium">{name}</span>
        {price !== undefined ? (
          <span className="ml-1.5 tabular-nums">{price} tokens</span>
        ) : null}
        {!isAvailable && <Pill tone="amber">draft</Pill>}
        {isDirectPay && <span className="ml-1.5"><Pill tone="blue">direct pay</Pill></span>}
      </Row>
      {vendor || booth ? (
        <MutedRow>
          at {vendor || 'vendor'}
          {booth ? ` · ${booth}` : ''}
        </MutedRow>
      ) : null}
      {dietaryFlags.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {dietaryFlags.map((label) => (
            <Pill key={label} tone="neutral">{label}</Pill>
          ))}
        </div>
      ) : null}
      {isAvailable ? (
        <MutedRow>Live on the menu now.</MutedRow>
      ) : (
        <MutedRow>Created as a draft — flip availability to publish.</MutedRow>
      )}
    </div>
  )
}

function CreateVendorSummary({ data }: { data: Record<string, unknown> }) {
  const name = clean(data.vendor_name as string | undefined) || `Vendor #${data.vendor_id}`
  const booth = clean(data.booth as string | undefined)
  const walkUp = data.walk_up !== false
  const orderAhead = !!data.order_ahead

  return (
    <div className="space-y-0.5">
      <Row icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}>
        <span className="font-medium">{name}</span>
        {walkUp && <span className="ml-1.5"><Pill tone="neutral">walk-up</Pill></span>}
        {orderAhead && <span className="ml-1.5"><Pill tone="blue">order ahead</Pill></span>}
      </Row>
      {booth && booth !== name ? <MutedRow>booth {booth}</MutedRow> : null}
      <MutedRow>Live at this event — no menu items yet.</MutedRow>
    </div>
  )
}

function VoucherRedemptionSummary({ data }: { data: Record<string, unknown> }) {
  const matchedBy = data.matched_by as string | undefined
  const name = clean(data.voucher_name as string | undefined)
  const value = typeof data.voucher_value === 'number' ? data.voucher_value : undefined
  const voucherType = data.voucher_type as string | undefined
  const maxUses = data.max_uses as number | null | undefined
  const maxUsesPerUser = data.max_uses_per_user as number | null | undefined
  const expiry = data.expiry as string | undefined
  const hideFlag = !!data.hide_voucher_name
  const ambiguousCount = typeof data.ambiguous_count === 'number' ? data.ambiguous_count : 0

  if (matchedBy === 'none') {
    return (
      <Row icon={<Gift className="h-3.5 w-3.5 text-neutral-500" />}>
        <span className="text-neutral-500 dark:text-neutral-400">
          Couldn&apos;t identify the voucher — no claim row in window and no name in the transaction.
        </span>
      </Row>
    )
  }

  if (matchedBy === 'description_fallback') {
    return (
      <div className="space-y-0.5">
        <Row icon={<Gift className="h-3.5 w-3.5 text-amber-600" />}>
          <span className="font-medium">{name || '(unnamed voucher)'}</span>
          <span className="ml-1.5"><Pill tone="amber">parsed from description</Pill></span>
        </Row>
        <MutedRow>
          No matching VoucherClaim row — only the name was recoverable.
        </MutedRow>
      </div>
    )
  }

  // matched_by is either 'voucher_claim' (clean match) or 'ambiguous_pick_nearest'.
  // Show the same primary row but add an amber "ambiguous" pill so the operator
  // knows to double-check with the attendee.
  return (
    <div className="space-y-0.5">
      <Row icon={<Gift className="h-3.5 w-3.5 text-emerald-600" />}>
        <span className="font-medium">{name || '(unnamed voucher)'}</span>
        {value !== undefined ? (
          <span className="ml-1.5 tabular-nums">{value} tokens</span>
        ) : null}
        {voucherType ? <span className="ml-1.5"><Pill tone="neutral">{voucherType}</Pill></span> : null}
        {hideFlag && <span className="ml-1.5"><Pill tone="neutral">name hidden from attendees</Pill></span>}
        {matchedBy === 'ambiguous_pick_nearest' && (
          <span className="ml-1.5"><Pill tone="amber">ambiguous match</Pill></span>
        )}
      </Row>
      {matchedBy === 'ambiguous_pick_nearest' && ambiguousCount > 1 ? (
        <MutedRow>
          {ambiguousCount} vouchers with this value were claimed in the same window — verify with the attendee.
        </MutedRow>
      ) : null}
      {(maxUses !== null && maxUses !== undefined) || (maxUsesPerUser !== null && maxUsesPerUser !== undefined) || expiry ? (
        <MutedRow>
          {maxUses !== null && maxUses !== undefined ? <>Max uses: {maxUses}. </> : null}
          {maxUsesPerUser !== null && maxUsesPerUser !== undefined ? <>Per user: {maxUsesPerUser}. </> : null}
          {expiry ? <>Expires {new Date(expiry).toLocaleDateString()}.</> : null}
        </MutedRow>
      ) : null}
    </div>
  )
}

function sourceLabel(s: PermissionSource): string {
  switch (s.type) {
    case 'organization_admin':
      return `Organization admin · ${clean(s.organization_name) || ''}`
    case 'event_admin':
      return 'Event admin (this event)'
    case 'vendor_admin':
      return `Vendor admin · ${clean(s.vendor_name) || ''} (all events)`
    case 'vendor_event_admin':
      return `Vendor-event admin · ${clean(s.vendor_name) || ''} (this event only)`
  }
}

// ============================================================================
// Fallback
// ============================================================================

function GenericSummary({ data }: { data: Record<string, unknown> }) {
  const keys = Object.keys(data).slice(0, 6)
  return (
    <Row icon={<Inbox className="h-3.5 w-3.5" />}>
      <span className="text-neutral-500 dark:text-neutral-400">
        Returned: {keys.join(', ')}
        {Object.keys(data).length > keys.length ? '…' : ''}
      </span>
    </Row>
  )
}

